import { useMemo } from "react"

import { PROJECT_TYPES, PROJECT_STATE, INVESTMENT_STATUS } from "@components/Domain/Constants"
import getStatistics, { OUTGOING_TOTAL_KEY, INCOMING_TOTAL_KEY } from "@helpers/getStatistics"


const extendProjects = (projects, investments, transactions) => {
  for (const project of projects) {
    project.investments = investments
      .filter(({ projectId }) => project.id === projectId)

    const projectTransactions = transactions
      .filter(({ projectId }) => project.id === projectId)

    const statistics = getStatistics(projectTransactions)

    project.capitalInvested = statistics[OUTGOING_TOTAL_KEY] || 0
    project.receiveDistribution = statistics[INCOMING_TOTAL_KEY] || 0
  }
}


const isProjectOpen = project =>
  project.state === PROJECT_STATE.OPEN


const isOpenFundProject = project =>
  isProjectOpen(project) && project.type === PROJECT_TYPES.FUND


const isProjectClosed = project =>
  project.state === PROJECT_STATE.CLOSED


const isProjectArchived = project =>
  project.state === PROJECT_STATE.ARCHIVED


const hasPartnerNonFundedInvestments = project =>
  project.investments
    .filter(({ isPartner, isFunded }) => isPartner && !isFunded)
    .length > 0


const hasNonRejectedInvestments = project =>
  project.investments
    .filter(({ status }) => status !== INVESTMENT_STATUS.REJECTED)
    .length > 0


const hasActiveInvestments = project =>
  project.investments
    .filter(({ status }) => status !== INVESTMENT_STATUS.REJECTED)
    .length > 0


const computeGroupedProjects = (projects = [], investments = [], transactions =[]) => {
  extendProjects(projects, investments, transactions)

  const openProjects = projects
    .filter(hasNonRejectedInvestments)
    .filter(project => isProjectOpen(project) || hasPartnerNonFundedInvestments(project))

  const closedProjects = projects
    .filter(hasActiveInvestments)
    .filter(project => isProjectClosed(project) || isOpenFundProject(project))
    .filter(project => !hasPartnerNonFundedInvestments(project))

  const archivedProjects = projects
    .filter(isProjectArchived)
    .filter(hasActiveInvestments)

  return {
    openProjects,
    closedProjects,
    archivedProjects,
  }
}


const useGroupedProjects = (projects, investments, transactions) => {
  const groupedProjects = useMemo(
    () => computeGroupedProjects(projects, investments, transactions)
  , [ projects, investments, transactions ])

  return groupedProjects
}

export default useGroupedProjects
